import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  isDestructive = true,
  defaultFocus = 'cancel',
  loading = false,
  language = 'english'
}) {
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (defaultFocus === 'cancel' && cancelRef.current) cancelRef.current.focus();
      else if (defaultFocus === 'confirm' && confirmRef.current) confirmRef.current.focus();
    }
  }, [open, defaultFocus]);

  if (!open) return null;
  const dir = language === 'hebrew' ? 'rtl' : 'ltr';

  const modal = (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/20 dark:bg-ink/40 transition-opacity animate-in fade-in"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      dir={dir}
    >
      <div 
        className="bg-surface dark:bg-surface-dark w-full max-w-sm rounded-md border border-line dark:border-line-dark p-6"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-ink dark:text-ink-dark mb-2">{title}</h2>
        {body && <div className="text-sm text-ink-muted dark:text-ink-muted-dark mb-6">{body}</div>}
        
        <div className="flex gap-3 justify-end">
          <button
            ref={cancelRef}
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 min-h-11 rounded-md border border-line dark:border-line-dark text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-forest"
          >
            {language === 'hebrew' ? 'ביטול' : 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 min-h-11 rounded-md text-white text-sm font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-canvas-dark ${
              isDestructive
                ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-600'
                : 'bg-forest hover:bg-forest-hover focus-visible:ring-forest'
            }`}
          >
            {loading ? (language === 'hebrew' ? 'טוען...' : 'Loading...') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
